import React from "react";

const DISMISSED_HINTS_KEY = "trainingPlansV2.dismissedHints";
const LEGACY_EDITOR_ALERTS_KEY = "trainingPlansV2.dismissedAlertsByPlan";

/** Per training-plans screen; hint ids are unique within a scope — never include plan id (power users shouldn’t re-see the same copy per plan). */
const SCOPES = new Set(["editor", "manage", "assign", "library"]);
const GLOBAL_PLAN_KEY = "global";

export function compositeKey(scope, hintId) {
  return `${scope}|${GLOBAL_PLAN_KEY}|${hintId}`;
}

/**
 * Older storage used `scope|planId|hintId` so dismissals didn’t carry across plans.
 * Collapse to `scope|global|hintId`.
 */
export function migrateToGlobalKeys(raw) {
  if (!raw || typeof raw !== "object") {
    return {};
  }
  const next = { ...raw };
  let changed = false;
  Object.keys(raw).forEach((k) => {
    if (!raw[k]) {
      return;
    }
    const parts = k.split("|");
    if (parts.length !== 3 || !SCOPES.has(parts[0])) {
      return;
    }
    const [scope, , hintId] = parts;
    const newKey = compositeKey(scope, hintId);
    if (newKey !== k) {
      next[newKey] = true;
      delete next[k];
      changed = true;
    }
  });
  return changed ? next : raw;
}

function persistDismissedMap(map) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(DISMISSED_HINTS_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

function mergeLegacyEditorInto(merged) {
  if (typeof window === "undefined") {
    return { merged, migrated: false };
  }
  try {
    const raw = window.localStorage.getItem(LEGACY_EDITOR_ALERTS_KEY);
    if (!raw) {
      return { merged, migrated: false };
    }
    const legacy = JSON.parse(raw);
    if (!legacy || typeof legacy !== "object") {
      return { merged, migrated: false };
    }
    const next = { ...merged };
    let migrated = false;
    Object.entries(legacy).forEach(([, byHint]) => {
      if (!byHint || typeof byHint !== "object") {
        return;
      }
      Object.entries(byHint).forEach(([hintId, value]) => {
        if (value) {
          const k = compositeKey("editor", hintId);
          if (!next[k]) {
            next[k] = true;
            migrated = true;
          }
        }
      });
    });
    return { merged: next, migrated };
  } catch {
    return { merged, migrated: false };
  }
}

function loadRawDismissedMap() {
  if (typeof window === "undefined") {
    return {};
  }
  let parsed = {};
  try {
    const raw = window.localStorage.getItem(DISMISSED_HINTS_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      if (p && typeof p === "object") {
        parsed = p;
      }
    }
  } catch {
    parsed = {};
  }

  const afterKeyMigration = migrateToGlobalKeys(parsed);
  const legacyMerge = mergeLegacyEditorInto(afterKeyMigration);
  const combined = legacyMerge.merged;
  if (legacyMerge.migrated || combined !== parsed) {
    persistDismissedMap(combined);
  }
  return combined;
}

/**
 * Dismissible training-plan hints: close persists in localStorage; use restoreAll() to show again (e.g. "Show hints").
 * Dismissals are per scope + hint id (not per plan) so power users don’t see the same explainer on every plan.
 *
 * @param {"editor"|"manage"|"assign"|"library"} scope
 */
export function useTrainingPlansV2Hints(scope) {
  const [dismissedMap, setDismissedMap] = React.useState(loadRawDismissedMap);

  const prefix = React.useMemo(() => `${scope}|${GLOBAL_PLAN_KEY}|`, [scope]);

  const hasAnyDismissedInScope = React.useMemo(() => {
    return Object.keys(dismissedMap).some((k) => k.startsWith(prefix));
  }, [dismissedMap, prefix]);

  const isDismissed = React.useCallback(
    (hintId) => !!dismissedMap[compositeKey(scope, hintId)],
    [dismissedMap, scope],
  );

  const dismiss = React.useCallback(
    (hintId) => {
      const key = compositeKey(scope, hintId);
      setDismissedMap((prev) => {
        if (prev[key]) {
          return prev;
        }
        const next = { ...prev, [key]: true };
        persistDismissedMap(next);
        return next;
      });
    },
    [scope],
  );

  const restoreAll = React.useCallback(() => {
    setDismissedMap((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k.startsWith(prefix)) {
          delete next[k];
        }
      });
      persistDismissedMap(next);
      return next;
    });
  }, [prefix]);

  return {
    isDismissed,
    dismiss,
    restoreAll,
    hasAnyDismissedInScope,
  };
}
